import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authApi.login(formData.email, formData.password);
      
      // Handle API response structure: { success: true, data: { user: {...}, token: "..." } }
      if (response.data.success) {
        // Get token and user data from nested structure
        const token = response.data.data?.token || response.data.access_token;
        const userData = response.data.data?.user || response.data.user || response.data.data;
        
        // Handle "Remember Me" - store token in localStorage if checked, sessionStorage if not
        console.log('Remember me checked?', formData.remember); // Debug
        if (formData.remember) {
          localStorage.setItem('token', token);
          localStorage.setItem('remember', 'true');
          sessionStorage.removeItem('token'); // Clear sessionStorage if using localStorage
          console.log('Token stored in localStorage (persistent)');
        } else {
          // Use sessionStorage for non-persistent login (cleared when browser closes)
          sessionStorage.setItem('token', token);
          localStorage.removeItem('token'); // Clear localStorage if using sessionStorage
          localStorage.removeItem('remember');
          console.log('Token stored in sessionStorage (session only)');
        }
        
        // Store user data and token in context
        login(userData, token);
        
        // Redirect to home
        navigate('/');
      } else {
        setError(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Login" description="Log in to GuyRules to vote, submit rules, and access Club Guydom." canonicalPath="/login" />
      <Header />
      <div className="container">
        <div className="login-page">
          <h1>Login</h1>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="forgot-password-link">Forgot password?</Link>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <p className="register-link" style={{ marginTop: '1rem' }}>
            Don't have an account? <Link to="/register">Register here</Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Login;
